import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { supabase } from "../supabase";
import useSnackbarNotification from "../hooks/useSnackbarNotification";
import { FiRefreshCw } from "react-icons/fi";

const EmailVerificationPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { showError, showSuccess } = useSnackbarNotification();
    
    // Get email from location state or query params
    const email = location.state?.email || new URLSearchParams(location.search).get("email") || "";
    const [loading, setLoading] = useState(false);

    const handleResendEmail = async () => {
        if (!email) {
            showError("Email address not found. Please sign up again.");
            return;
        }

        setLoading(true);
        try {
            // Get the current origin for the redirect URL
            const currentOrigin = window.location.origin;
            const redirectUrl = `${currentOrigin}/role-selection`;

            const resendOptions = {};
            
            // Add emailRedirectTo for https domains (production: doctorsjobs.io)
            if (currentOrigin && 
                !currentOrigin.includes('localhost') && 
                !currentOrigin.includes('127.0.0.1') &&
                currentOrigin.startsWith('https://')) {
                resendOptions.emailRedirectTo = redirectUrl;
            }

            const { error } = await supabase.auth.resend({
                type: 'signup',
                email: email,
                options: resendOptions
            });

            if (error) throw error;

            showSuccess("Verification email has been resent! Please check your inbox (or spam folder).");
        } catch (error) {
            console.error("Resend email error:", error);
            showError("Failed to resend email. Please try again later or contact support.");
        } finally {
            setLoading(false);
        }
    };

    const handleContactSupport = () => {
        window.open("https://tally.so/r/wb6krg", "_blank");
    };

    return (
        <div className="min-h-screen bg-gray-100 flex items-center justify-center py-10 px-4">
            <div className="max-w-2xl w-full">
                <div
                    className="logo cursor-pointer absolute top-6 left-6"
                    onClick={() => navigate("/")}
                >
                    Doctors' Jobs <span className="logo-badge">Beta</span>
                </div>

                <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8">
                    <h2 className="text-center text-2xl font-bold text-gray-900 mb-4">
                        Create an account
                    </h2>

                    {/* Mailbox Illustration */}
                    <div className="flex justify-center mb-4">
                        <img
                            src="/Check your inbox.png"
                            alt="Check your inbox"
                            className="w-40 h-40 object-contain"
                        />
                    </div>

                    <h3 className="text-center text-xl font-bold text-gray-900 mb-3">
                        Verify Your Email To Get Started
                    </h3>

                    <p className="text-center text-sm text-gray-600 mb-6 leading-relaxed">
                        Check your inbox (or spam folder) for the verification email or resend it. 
                        If you're not receiving it, please contact our{" "}
                        <button
                            onClick={handleContactSupport}
                            className="underline text-gray-900 hover:text-gray-700 font-medium"
                        >
                            support team
                        </button>{" "}
                        for assistance.
                    </p>

                    {email && (
                        <p className="text-center text-xs text-gray-500 mb-4">
                            Email sent to: <span className="font-medium">{email}</span>
                        </p>
                    )}

                    <div className="flex justify-center">
                        <button
                            onClick={handleResendEmail}
                            disabled={loading}
                            className="flex items-center gap-2 px-6 py-3 bg-gray-800 text-white rounded-full font-medium hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                            <FiRefreshCw className={`w-4 h-4 text-white ${loading ? 'animate-spin' : ''}`} />
                            {loading ? "Sending..." : "Resend email"}
                        </button>
                    </div>

                    <div className="mt-6 text-center">
                        <button
                            onClick={() => navigate("/login")}
                            className="text-sm text-gray-900 hover:text-black font-medium"
                        >
                            Already verified? Go to login
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EmailVerificationPage;

